/**
 * WhatsApp's text formatting, parsed into a tree the inbox can render without
 * ever handing message text to the DOM as HTML.
 *
 *   *bold*  _italic_  ~strike~  `code`  ```block```  and bare links
 *
 * The rules follow WhatsApp's: a marker only counts when it hugs a word on the
 * inside and is not glued to a word on the outside, so `2*3*4`, snake_case and
 * an email address are left alone. Markers nest (`*bold _and italic_*`), but a
 * marker never spans a line break. SMS and email have no formatting, so they
 * use `markup: false` and only get their links.
 */

export type Node =
  | { kind: 'text'; text: string }
  | { kind: 'link'; href: string; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'block'; text: string }
  | { kind: 'bold' | 'italic' | 'strike'; children: Node[] };

const LINK = /https?:\/\/[^\s<>"']+/;
// Punctuation that ends a sentence rather than the link it follows.
const TRAILING = /[.,;:!?)\]}'"]+$/;
const STYLES = { '*': 'bold', _: 'italic', '~': 'strike' } as const;
// Marker, then text that starts and ends with a non-space, then the same
// marker; neither marker may touch a word character on its outer side.
const STYLED = /(?<![\p{L}\p{N}*_~])([*_~])(\S(?:[^\n]*?\S)?)\1(?![\p{L}\p{N}*_~])/u;
const INLINE_CODE = /`([^`\n]+)`/;

function links(text: string): Node[] {
  const out: Node[] = [];
  let rest = text;
  for (let m = LINK.exec(rest); m; m = LINK.exec(rest)) {
    const raw = m[0];
    const href = raw.replace(TRAILING, '');
    if (m.index) out.push({ kind: 'text', text: rest.slice(0, m.index) });
    out.push({ kind: 'link', href, text: href });
    rest = rest.slice(m.index + href.length);
  }
  if (rest) out.push({ kind: 'text', text: rest });
  return out;
}

function inline(text: string, markup: boolean): Node[] {
  if (!markup) return links(text);
  // Earliest of: a link, inline code, a styled run. Links first among ties,
  // so a URL containing _ or * is never read as formatting.
  const candidates = [
    { kind: 'link' as const, m: LINK.exec(text) },
    { kind: 'code' as const, m: INLINE_CODE.exec(text) },
    { kind: 'styled' as const, m: STYLED.exec(text) },
  ].filter((c) => c.m) as { kind: 'link' | 'code' | 'styled'; m: RegExpExecArray }[];
  if (!candidates.length) return text ? [{ kind: 'text', text }] : [];
  const first = candidates.reduce((a, b) => (b.m.index < a.m.index ? b : a));
  const { m } = first;

  let node: Node;
  let length = m[0].length;
  if (first.kind === 'link') {
    const href = m[0].replace(TRAILING, '');
    length = href.length;
    node = { kind: 'link', href, text: href };
  } else if (first.kind === 'code') {
    node = { kind: 'code', text: m[1] };
  } else {
    node = { kind: STYLES[m[1] as keyof typeof STYLES], children: inline(m[2], true) };
  }
  const before = text.slice(0, m.index);
  return [
    ...(before ? [{ kind: 'text', text: before } as Node] : []),
    node,
    ...inline(text.slice(m.index + length), true),
  ];
}

export function parseMessage(text: string, { markup = true } = {}): Node[] {
  if (!text) return [];
  if (!markup) return links(text);
  const out: Node[] = [];
  // Code blocks first: nothing inside them is formatting.
  const parts = text.split(/```([\s\S]*?)```/);
  parts.forEach((part, i) => {
    if (i % 2) out.push({ kind: 'block', text: part.replace(/^\n|\n$/g, '') });
    else out.push(...inline(part, true));
  });
  return out;
}
